<template>
  <div class="container mt-3">
    
    <div class="desktop-view">
      <div class="search-container mb-2">
        <button
          type="button" 
          class="btn btn-primary btn-sm ms-2" 
          data-tippy-content="refresh" 
          @click="loadAllData"
><font-awesome-icon icon="sync-alt"/></button>
        
        <SearchBox 
          class="ms-3"
          tool-tip="search by name, lrr id, lrr uuid, supplier-id or gateway name"
          @search="newSearchText($event)" 
          :search-term="searchTerm"
/>

        <PageNav
v-show="showPageNav" 
          :num-pages="pageList.length"
          ref="pageNav"
          @page-change="changePage($event)"
/>

          <!-- Tag filter dropdown button -->
          <div class="dropdown ms-2 me-2">
            <button
              ref="dropdownRef"
              class="btn btn-outline-secondary btn-sm dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Filter Tags
            </button>
            <div
              class="dropdown-menu px-3 pt-0 pb-3 shadow-sm"
              style="min-width: 300px; max-height: 300px; overflow-y: auto;"
              @click.stop
>
              <table class="table table-sm table-borderless mb-0">
                <thead class="thead-light sticky-header">
                  <tr>
                    <th class="text-center" style="width: 40px;" data-tippy-content="Include tag">✔</th>
                    <th class="text-center">Tags</th>
                    <th class="text-center" style="width: 40px;" data-tippy-content="Exclude tag">✖</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tag in allTags" :key="tag">
                    <td class="text-center">
                      <input
                        type="checkbox"
                        :value="tag"
                        v-model="tempIncludeTags"
                        :disabled="tempExcludeTags.includes(tag)"
                      >
                    </td>
                    <td class="text-center">{{ tag }}</td>
                    <td class="text-center">
                      <input
                        type="checkbox"
                        :value="tag"
                        v-model="tempExcludeTags"
                        :disabled="tempIncludeTags.includes(tag)"
                      >
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="mt-3 d-flex justify-content-between">
                <button class="btn btn-sm btn-outline-secondary" @click="clearFilters">Clear</button>
                <button class="btn btn-sm btn-success" @click="applyFilters">Apply</button>
              </div>
            </div>
          </div>
      </div>

      <div v-if="showRows">
        <AlertRow 
          v-for="(alert, index) in curPage.alertList" 
          
          :key="alert.alertId" 
          :alert="alert"
          @clear-alert="clearAlert($event)"
          :disabled="httpClearBusy"
          :index="index"
/>
      </div> 
    </div>

    <div class="mobile-view">
      <div class="search-container-phone mb-2">

        <PageNav
v-show="showPageNav" 
          :num-pages="pageList.length"
          ref="pageNav"
          class="m-1"
          @page-change="changePage($event)"
/>

        <button
          type="button" 
          class="btn btn-primary btn-sm m-1" 
          @click="loadAllData"
><font-awesome-icon icon="sync-alt"/> refresh</button>
        
        <SearchBox 
          class="m-1"
          tool-tip="search by lrr id, supplier-id or gateway name"
          @search="newSearchText($event)" 
          :search-term="searchTerm"
/>
        
      </div>
      <div v-if="showRows">
        <AlertRowMobile
          v-for="(alert, index) in curPage.alertList" 
          :key="alert.alertId" 
          :alert="alert"
          @clear-alert="clearAlert($event)"
          :disabled="httpClearBusy"
          :index="index"
/>
      </div> 
    </div>

    <Loading v-if="httpBusy"/>

    <Error :label="error"/>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { curPref } from '@/services/Preferences';
import { AlertsPage } from '@/types';
import Error from '@/components/Error.vue';
import Loading from '@/components/Loading.vue';
import SearchBox from '@/components/SearchBox.vue';
import PageNav from '@/components/PageNav.vue';
import AlertRow from '@/components/alerts/AlertRow.vue';
import AlertRowMobile from '@/components/alerts/AlertRowMobile.vue';
import { ApiService, curApiService } from '@/services/ApiService';
import { ApiClient, curApiClient } from '@/services/ApiClient';

const httpBusy = ref(false);
const httpClearBusy = ref(false);
const error = ref('');
const allPageList = ref<AlertsPage[]>([]); // Store all data
const pageList = ref<AlertsPage[]>([]);
const curPage = ref<AlertsPage>({} as AlertsPage);
const searchTerm = ref(curPref.retrieve('alerts-search-term', ''));

// tag filters
const allTags = ref<string[]>([]);
const selectedIncludeTags = ref<string[]>([]);
const selectedExcludeTags = ref<string[]>([]);

const tempIncludeTags = ref<string[]>([]);
const tempExcludeTags = ref<string[]>([]);

const pageNav = ref<InstanceType<typeof PageNav>>();

// services
const apiService: ApiService = curApiService;
const apiClient: ApiClient = curApiClient;

const dropdownRef = ref<HTMLElement>();

onMounted(() => {
  // selectedIncludeTags.value = ['Production'];
  // selectedExcludeTags.value = ['test'];

  tempIncludeTags.value = [...selectedIncludeTags.value];
  tempExcludeTags.value = [...selectedExcludeTags.value];

  loadAllData();
});

async function loadAllData() {
  httpBusy.value = true;
  const reply = await apiService.getActiveAlerts({
    searchTerm: '', // Load all data
    includeTags: [],
    excludeTags: [],
  });
  httpBusy.value = false;
  
  if (reply.errorMsg) {
    return error.value = `Unable to load active alerts ${reply.errorMsg}`;
  }

  allPageList.value = reply.pageList!;
  allTags.value = reply.allTags || [];
  
  // Apply initial filtering
  applyLocalFiltering();
}

function applyLocalFiltering() {
  // Get all alerts from all pages
  const allAlerts = allPageList.value.flatMap(page => page.alertList);
  
  // Apply search filter
  let filteredAlerts = allAlerts.filter(alert => {
    if (!searchTerm.value) return true;
    const searchLower = searchTerm.value.toLowerCase();
    return alert.baseStation.lrrId.toLowerCase().includes(searchLower) ||
           alert.baseStation.name.toLowerCase().includes(searchLower) ||
           alert.baseStation.supplierId.toLowerCase().includes(searchLower);
  });
  
  // Apply tag filters
  if (selectedIncludeTags.value.length > 0) {
    filteredAlerts = filteredAlerts.filter(alert => 
      alert.baseStation.tags && selectedIncludeTags.value.some(tag => alert.baseStation.tags!.includes(tag))
    );
  }
  
  if (selectedExcludeTags.value.length > 0) {
    filteredAlerts = filteredAlerts.filter(alert => 
      !alert.baseStation.tags || !selectedExcludeTags.value.some(tag => alert.baseStation.tags!.includes(tag))
    );
  }
  
  // Recreate pages with filtered data
  const chunkedList = [];
  for (let i = 0; i < filteredAlerts.length; i += 50) {
    chunkedList.push(filteredAlerts.slice(i, i + 50));
  }
  
  pageList.value = chunkedList.map((chunk, index) => ({
    pageNum: index,
    alertList: chunk
  }));

  // Set current page
  if (pageList.value.length > 0) {
    pageNav.value?.reset();
    curPage.value = pageList.value[0];
  }
}

function newSearchText(newSearchText: string) {
  curPref.store('alerts-search-term', newSearchText);
  searchTerm.value = newSearchText;
  applyLocalFiltering();
}

const showPageNav = computed((): boolean => {
  return pageList.value.length > 0 && !httpBusy.value;
});

const showRows = computed((): boolean => {
  return pageList.value.length > 0 && !httpBusy.value;
});

function changePage(newPageNum: number) {
  curPage.value = pageList.value[newPageNum];
}

async function clearAlert(alertId: string) {
  httpClearBusy.value = true;
  const reply = await apiClient.clearAlert({ alertId: alertId });
  httpClearBusy.value = false;
  
  if (reply.errorMsg) {
    return error.value = `Unable to clear alert ${alertId}: ${reply.errorMsg}`;
  }

  applyLocalFiltering();
}

function applyFilters() {
  selectedIncludeTags.value = [...tempIncludeTags.value];
  selectedExcludeTags.value = [...tempExcludeTags.value];
  applyLocalFiltering();
  closeDropdown();
}

function clearFilters() {
  tempIncludeTags.value = [];
  tempExcludeTags.value = [];
  selectedIncludeTags.value = [];
  selectedExcludeTags.value = [];
  applyLocalFiltering();
  closeDropdown();
}

function closeDropdown() {
  if (dropdownRef.value) {
    dropdownRef.value.click(); // toggles the dropdown closed
  }
}

// Watch for changes in temporary filter tags and update immediately
watch([tempIncludeTags, tempExcludeTags], () => {
  selectedIncludeTags.value = [...tempIncludeTags.value];
  selectedExcludeTags.value = [...tempExcludeTags.value];
  applyLocalFiltering();
}, { deep: true });
</script>


<style scoped lang="scss">

.search-container {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
}

.search-container-phone {
  width: 90%;
  margin-right: auto;
  margin-left: auto;
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;
}

input[type="checkbox"] {
  -webkit-appearance: checkbox !important;
  appearance: checkbox !important;
}

.dropdown-menu {
  z-index: 1000;
  background-color: #fff;
}

.dropdown-menu table th {
  font-weight: 600;
}

.sticky-header th {
  position: sticky;
  top: 0;
  background-color: #fff; 
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.sticky-header th {
  border-bottom: 1px solid #dee2e6;
}

/* Responsive visibility */
.desktop-view {
  display: block;
}

.mobile-view {
  display: none;
}

@media (max-width: 768px) {
  .desktop-view {
    display: none;
  }
  
  .mobile-view {
    display: block;
  }
}

</style>